/**
 * Raycast suspension — four wheels, one ray each, along the car's local -up.
 *
 * This is the RocketSim/Bullet `btRaycastVehicle` model with the RocketSim
 * calibration (SUSPENSION in ./constants). RLUtilities has no suspension model
 * at all, so there is no second source for any of it.
 *
 * Geometry, in the order the numbers compose:
 *
 *   hardpoint      = car position + R * WHEEL_OFFSET            (attachment)
 *   ray            = hardpoint, along -up, length restLength + radius
 *   suspensionLen  = hitDistance - radius                       (clamped to travel)
 *   compression    = springRestLength - suspensionLen
 *   springRestLen  = restLength - maxTravel                     (26.755 / 25.055)
 *
 * `restLengthFront/Back` are the FULLY EXTENDED lengths; RocketSim subtracts
 * `maxTravel` from them before handing them to Bullet, which is why the spring's
 * zero-force length is 12 uu shorter than the constant's name suggests.
 *
 * Force, per wheel, expressed directly as an acceleration on the chassis:
 *
 *   a = (stiffness * compression - damping * relVel) * forceScale / CAR_MASS
 *
 * `forceScale` is a per-wheel share of the car's mass — 2*35.75 + 2*54.265 =
 * 180.03 ~ CAR_MASS — so the sum over four wheels reduces to `stiffness *
 * compression`, i.e. stiffness is an acceleration per uu of compression.
 * `damping` switches between the compression (25) and relaxation (40) values on
 * the sign of the contact-normal-relative velocity, exactly as Bullet does, and
 * the total is clamped at zero because a wheel cannot pull on the ground.
 *
 * Two numbers fall out of this and are worth stating, because they are the only
 * validation the suspension block has ever had (docs/CONSTANTS_SOURCES.md U13
 * notes the force scales "were never validated against the resulting
 * equilibrium ride height of 17 uu"):
 *
 *   - The front/back moment arms and force scales satisfy
 *     51.25 * 35.75 = 1832.2 and 33.75 * 54.265 = 1831.4 — equal to 0.04 %.
 *     Pitch balance therefore requires EQUAL compression front and back, which
 *     is clearly deliberate.
 *   - Balancing GRAVITY + STICKY_FORCE_GROUND_ACCEL = 975 uu/s^2 against the
 *     springs puts the car's origin at z = 17.033, against the independently
 *     sourced CAR_SPAWN_REST_Z = 17. That 0.03 uu agreement only appears if the
 *     sticky force is CONTINUOUS on the ground, which is RocketSim's model and
 *     the disputed half of U5.
 *
 * The force is applied at the contact point, not at the centre of mass, so it
 * produces pitch and roll torque — that is what makes the car nose over on
 * landing and settle 0.54 degrees nose-down at rest.
 *
 * ===========================================================================
 * PER BODY
 * ===========================================================================
 *
 * Wheel radii, attachment points and rest lengths are properties of the CAR
 * BODY (../physics/car-config), so the four `WheelSpec`s are derived from
 * `car.config` rather than being a module constant. The derivation is memoized
 * per config object — the presets are frozen singletons, so the per-tick cost
 * is one WeakMap lookup and the table itself is built once per body per
 * process. Everything else in SUSPENSION (stiffness, damping, travel, force
 * scales) is global in RocketSim and stays in ./constants.
 *
 * The force scales, and therefore the pitch-balance argument above, are the
 * Octane's. RocketSim applies the same two global scales to every body, so a
 * Dominus balances at whatever compression its own moment arms imply rather
 * than at equal front/back compression; that is RocketSim's behaviour, not an
 * approximation of ours.
 */

import { carInertiaPerMass, OCTANE_CONFIG, type CarConfig } from './car-config';
import type { CarState } from './car-state';
import { CAR_MASS, SUSPENSION } from './constants';
import { addScaledTo, clamp, copy, set, vec3, type Vec3 } from './math';
import type { ArenaGeometry, RayHit } from './surface';

/** Fixed per-wheel geometry. */
export interface WheelSpec {
  /** Attachment point in the car's local frame (columns forward, left, up). */
  readonly localOffset: Vec3;
  readonly radius: number;
  /** Fully extended suspension length (RocketSim's `restLength`). */
  readonly restLength: number;
  /** Zero-force spring length = restLength - maxTravel. */
  readonly springRestLength: number;
  /** Per-wheel share of CAR_MASS. */
  readonly forceScale: number;
  readonly front: boolean;
}

/** Per-wheel result of one suspension query. */
export interface WheelContact {
  /**
   * The wheel this result describes. REBOUND each tick from the car's own body
   * (see `carWheelSpecs`), not copied — a result buffer is reused across cars
   * and two cars may be different bodies, so a spec captured at construction
   * time would describe the wrong wheel.
   */
  spec: WheelSpec;
  /** Attachment point in world space. */
  hardpoint: Vec3;
  inContact: boolean;
  /** Contact point and normal; meaningless when `inContact` is false. */
  hit: RayHit;
  /** Current suspension length, clamped to the travel range. NaN when no hit. */
  suspensionLength: number;
  /** springRestLength - suspensionLength; negative while extended past rest. */
  compression: number;
  /** Chassis speed along the contact normal at the contact point; < 0 compressing. */
  relativeVelocity: number;
  /** Magnitude of the resulting chassis acceleration, uu/s^2. Never negative. */
  accelMagnitude: number;
}

export interface SuspensionResult {
  readonly wheels: readonly WheelContact[];
  contactCount: number;
  /** Mean unit contact normal, or null when no wheel touches anything. */
  normal: Vec3 | null;
  /**
   * Storage behind `normal`, owned by this result. `normal` is either null or
   * this exact object, so a reused result never has to allocate a vector and
   * two results in flight never share one.
   */
  readonly normalStore: Vec3;
  /** Summed linear acceleration from all four springs, world frame. */
  readonly linearAccel: Vec3;
  /** Summed angular acceleration about the centre of mass, world frame. */
  readonly angularAccel: Vec3;
}

/**
 * An empty result for `computeSuspension` to fill.
 *
 * The per-tick caller (./ground) keeps ONE of these and passes it every tick.
 * Building it fresh meant four `WheelContact` objects, four `RayHit`s and about
 * twenty `Vec3`s per car per tick, all dead by the end of the tick — the single
 * largest allocation site left in the simulation once the orientation
 * integration stopped allocating.
 */
export const makeSuspensionResult = (): SuspensionResult => ({
  // Every body has four simulated wheels — RocketSim's `threeWheels` is a
  // rendering fact, not a simulation one (see ./car-config) — so the buffer's
  // size is fixed and only the specs vary. The Octane's are a placeholder;
  // `computeSuspension` rebinds them from the car's own body every tick.
  wheels: OCTANE_WHEELS.map((spec) => ({
    spec,
    hardpoint: vec3(0, 0, 0),
    inContact: false,
    hit: { distance: 0, point: vec3(0, 0, 0), normal: vec3(0, 0, 1) },
    suspensionLength: NaN,
    compression: 0,
    relativeVelocity: 0,
    accelMagnitude: 0,
  })),
  contactCount: 0,
  normal: null,
  normalStore: vec3(0, 0, 1),
  linearAccel: vec3(0, 0, 0),
  angularAccel: vec3(0, 0, 0),
});

const makeWheel = (config: CarConfig, front: boolean, mirror: boolean): WheelSpec => {
  const pair = front ? config.frontWheels : config.backWheels;
  const o = pair.connectionOffset;
  const restLength = pair.suspensionRestLength;
  return {
    localOffset: vec3(o.x, mirror ? -o.y : o.y, o.z),
    radius: pair.radius,
    restLength,
    springRestLength: restLength - SUSPENSION.maxTravel,
    forceScale: front ? SUSPENSION.forceScaleFront : SUSPENSION.forceScaleBack,
    front,
  };
};

const wheelCache = new WeakMap<CarConfig, readonly WheelSpec[]>();

/**
 * The four wheels of a body, +Y first: front+Y, front-Y, back+Y, back-Y.
 *
 * RocketSim calls its +Y wheel the RIGHT one and we call column 1 `left`; the
 * component VALUES are identical either way (see docs/PHYSICS_REFERENCE.md §6)
 * and every stored pair is mirror-symmetric in Y, so the naming only matters
 * for reading debug output.
 *
 * Memoized on the config OBJECT. The presets are frozen singletons so this is
 * one table per body for the life of the process, and the per-tick cost is a
 * single WeakMap lookup rather than four object allocations.
 */
export const carWheelSpecs = (config: CarConfig): readonly WheelSpec[] => {
  const cached = wheelCache.get(config);
  if (cached !== undefined) return cached;
  const specs: readonly WheelSpec[] = Object.freeze([
    makeWheel(config, true, false),
    makeWheel(config, true, true),
    makeWheel(config, false, false),
    makeWheel(config, false, true),
  ]);
  wheelCache.set(config, specs);
  return specs;
};

/** The Octane's four wheels. Kept under the old name because it is what the
 *  rest of the tree imports; a car reads `carWheelSpecs(car.config)`. */
export const OCTANE_WHEELS: readonly WheelSpec[] = carWheelSpecs(OCTANE_CONFIG);

/** @deprecated Octane-only alias of `OCTANE_WHEELS`. */
export const WHEELS: readonly WheelSpec[] = OCTANE_WHEELS;

/** Scratch, so the per-wheel maths allocates nothing. Single-threaded module. */
const _down = vec3(0, 0, 0);
const _hardpoint = vec3(0, 0, 0);
const _normalSum = vec3(0, 0, 0);
const _contactArm = vec3(0, 0, 0);

/**
 * Cast all four wheels and accumulate the resulting chassis accelerations.
 * Pure with respect to `car`: nothing on it is mutated.
 *
 * `out` is filled and returned. It defaults to a fresh result so that a caller
 * holding two at once — a test comparing a grounded car against an airborne one
 * — still works; the per-tick caller passes its own and allocates nothing.
 */
export const computeSuspension = (
  car: CarState,
  arena: ArenaGeometry,
  out: SuspensionResult = makeSuspensionResult()
): SuspensionResult => {
  const m = car.orientation;
  const wheels = carWheelSpecs(car.config);
  const inertia = carInertiaPerMass(car.config);
  set(_down, -m[2], -m[5], -m[8]);
  set(out.linearAccel, 0, 0, 0);
  set(out.angularAccel, 0, 0, 0);
  set(_normalSum, 0, 0, 0);
  let contactCount = 0;

  for (let w = 0; w < wheels.length; w++) {
    const spec = wheels[w];
    const wheel = out.wheels[w];
    wheel.spec = spec;
    const o = spec.localOffset;
    set(
      _hardpoint,
      car.position.x + m[0] * o.x + m[1] * o.y + m[2] * o.z,
      car.position.y + m[3] * o.x + m[4] * o.y + m[5] * o.z,
      car.position.z + m[6] * o.x + m[7] * o.y + m[8] * o.z
    );
    copy(wheel.hardpoint, _hardpoint);

    // `wheel.hit` is offered as the output buffer; an arena that ignores the
    // hint returns its own object and the fields are copied below instead.
    const hit = arena.raycast(_hardpoint, _down, spec.restLength + spec.radius, wheel.hit);
    if (!hit) {
      wheel.inContact = false;
      wheel.suspensionLength = NaN;
      wheel.compression = 0;
      wheel.relativeVelocity = 0;
      wheel.accelMagnitude = 0;
      continue;
    }

    contactCount++;
    const n = hit.normal;
    addScaledTo(_normalSum, n, 1);

    const travel = SUSPENSION.maxTravel;
    const suspensionLength = clamp(
      hit.distance - spec.radius,
      spec.springRestLength - travel,
      spec.springRestLength + travel
    );
    const compression = spec.springRestLength - suspensionLength;

    // Chassis velocity at the contact point, projected on the contact normal.
    // Negative means the suspension is being compressed.
    _contactArm.x = hit.point.x - car.position.x;
    _contactArm.y = hit.point.y - car.position.y;
    _contactArm.z = hit.point.z - car.position.z;
    const w0 = car.angularVelocity;
    const pvx = car.velocity.x + w0.y * _contactArm.z - w0.z * _contactArm.y;
    const pvy = car.velocity.y + w0.z * _contactArm.x - w0.x * _contactArm.z;
    const pvz = car.velocity.z + w0.x * _contactArm.y - w0.y * _contactArm.x;
    const relativeVelocity = n.x * pvx + n.y * pvy + n.z * pvz;

    const damping =
      relativeVelocity < 0 ? SUSPENSION.dampingCompression : SUSPENSION.dampingRelaxation;
    const accelMagnitude = Math.max(
      0,
      ((SUSPENSION.stiffness * compression - damping * relativeVelocity) * spec.forceScale) /
        CAR_MASS
    );

    addScaledTo(out.linearAccel, n, accelMagnitude);

    // tau = r x F, taken in the car's local frame so the diagonal inertia
    // divides componentwise. Our frame is right-handed (PHYSICS_REFERENCE §6.2)
    // so the textbook r x F is the correct expression here.
    const rx = m[0] * _contactArm.x + m[3] * _contactArm.y + m[6] * _contactArm.z;
    const ry = m[1] * _contactArm.x + m[4] * _contactArm.y + m[7] * _contactArm.z;
    const rz = m[2] * _contactArm.x + m[5] * _contactArm.y + m[8] * _contactArm.z;
    const fx0 = n.x * accelMagnitude;
    const fy0 = n.y * accelMagnitude;
    const fz0 = n.z * accelMagnitude;
    const fx = m[0] * fx0 + m[3] * fy0 + m[6] * fz0;
    const fy = m[1] * fx0 + m[4] * fy0 + m[7] * fz0;
    const fz = m[2] * fx0 + m[5] * fy0 + m[8] * fz0;
    const tx = (ry * fz - rz * fy) / inertia.roll;
    const ty = (rz * fx - rx * fz) / inertia.pitch;
    const tz = (rx * fy - ry * fx) / inertia.yaw;
    out.angularAccel.x += m[0] * tx + m[1] * ty + m[2] * tz;
    out.angularAccel.y += m[3] * tx + m[4] * ty + m[5] * tz;
    out.angularAccel.z += m[6] * tx + m[7] * ty + m[8] * tz;

    wheel.inContact = true;
    if (hit !== wheel.hit) {
      wheel.hit.distance = hit.distance;
      copy(wheel.hit.point, hit.point);
      copy(wheel.hit.normal, n);
    }
    wheel.suspensionLength = suspensionLength;
    wheel.compression = compression;
    wheel.relativeVelocity = relativeVelocity;
    wheel.accelMagnitude = accelMagnitude;
  }

  out.contactCount = contactCount;
  if (contactCount === 0) {
    out.normal = null;
  } else {
    const len = Math.sqrt(
      _normalSum.x * _normalSum.x + _normalSum.y * _normalSum.y + _normalSum.z * _normalSum.z
    );
    if (len < 1e-9) set(out.normalStore, 0, 0, 0);
    else set(out.normalStore, _normalSum.x / len, _normalSum.y / len, _normalSum.z / len);
    out.normal = out.normalStore;
  }
  return out;
};
